import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  Box, Flex, Text, Input, InputGroup, InputLeftElement, Select,
  Spinner, Icon, Button, Table, Thead, Tbody, Tr, Th, Td,
  SimpleGrid, Badge, useToast, HStack, IconButton,
  Alert, AlertIcon, AlertDescription,
} from '@chakra-ui/react';
import {
  ChevronLeftIcon, ChevronRightIcon, ArrowLeftIcon, ArrowRightIcon,
} from '@chakra-ui/icons';
import {
  FiSearch, FiRefreshCw, FiEye, FiFileText, FiClipboard, FiUser, FiDownload, FiX,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { getSurveyResponsesByCategory, getSurveyResponse, getSurveyResponseFilterFields } from 'services/surveyService';
import { getCustomerByUsername, getGroupNamesByRuts } from 'services/woo';
import { useUserAuth } from 'utils/site';
import { loadCategories, getCategoriesForRole, getLabelForCategory, canView } from 'services/surveyCategoriesService';
import { normalizeCourseName, codeToDisplayLabel, buildNormalizedGroupOptions } from 'utils/courseUtils';
import ModalSurveyResponse from 'components/Modal/ModalSurveyResponse';

// Cache de cursos a nivel de módulo — persiste entre renders y cambios de filtro
const _courseCache = new Map();
// RUT → ID de WordPress (el perfil se abre por ID, no por RUT)
const _wpIdCache = new Map();


// Pregunta oculta que guarda el username del entrevistador (usuario del sistema)
const INTERVIEWER_FIELD = "meta_data_extra['survey_interviewer_auth']";

const PER_PAGE_OPTIONS = [20, 50, 100];

// ── helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtRelative = (iso) => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'Hoy';
  if (days === 1) return 'Ayer';
  if (days < 7) return `Hace ${days} días`;
  if (days < 30) return `Hace ${Math.floor(days / 7)} sem.`;
  return fmtDate(iso);
};

const respondentFullName = (r) => {
  if (!r) return '—';
  const parts = [r.first_name, r.last_name].filter(Boolean);
  return parts.length ? parts.join(' ') : (r.username || '—');
};

const initials = (name = '') => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
};

const AVATAR_COLORS = ['#3A66C8', '#C56516', '#1E9E63', '#6849C4', '#C03F76', '#147F77', '#B5631A'];
const avatarColor = (str = '') => AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length];

// ── sub-components ─────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, accent }) => (
  <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="16px"
    p="18px 22px" boxShadow="0 1px 2px rgba(54,38,22,.05)">
    <Text fontSize="11px" fontWeight="700" color="ink.400" textTransform="uppercase"
      letterSpacing=".07em" mb="6px">{label}</Text>
    <Text fontSize="28px" fontWeight="800" letterSpacing="-0.03em" lineHeight="1"
      color={accent ? 'coral.500' : 'ink.900'}>{value ?? '—'}</Text>
    {sub && <Text fontSize="12px" color="ink.400" fontWeight="600" mt="4px">{sub}</Text>}
  </Box>
);

const Avatar = ({ name, size = 30 }) => (
  <Flex w={`${size}px`} h={`${size}px`} borderRadius="50%" flexShrink={0}
    bg={avatarColor(name)} align="center" justify="center"
    color="white" fontWeight="700" fontSize={`${Math.round(size * 0.38)}px`}>
    {initials(name)}
  </Flex>
);

// ── main component ─────────────────────────────────────────────────────────────

const AtencionView = () => {
  const [responses, setResponses]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);

  // filtros
  const [category, setCategory]             = useState('atencion');
  const [selectedForm, setSelectedForm]     = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [search, setSearch]                 = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy]                 = useState('fecha');
  const [sortDir, setSortDir]               = useState('desc');
  const [dateFrom, setDateFrom]             = useState('');
  const [dateTo, setDateTo]                 = useState('');
  const [exporting, setExporting]           = useState(false);

  // filtro por entrevistador: opciones [{ value: username, label: nombre }]
  const [interviewerOptions, setInterviewerOptions] = useState([]);
  const [interviewer, setInterviewer]               = useState('');   // username
  const [granularity, setGranularity]       = useState('day'); // 'day' | 'week' | 'month'

  // paginación
  const [page, setPage]                     = useState(1);
  const [perPage, setPerPage]               = useState(20);

  // enriquecimiento de curso
  const [courseMap, setCourseMap]           = useState(new Map());
  const [coursesLoading, setCoursesLoading] = useState(false);

  // modal — carga el survey response completo async (igual que FichasTab)
  const [isViewOpen, setIsViewOpen]         = useState(false);
  const [viewData, setViewData]             = useState(null);

  const toast    = useToast();
  const history  = useHistory();
  const { user } = useUserAuth();
  const [categories, setCategories] = useState([]);

  // cargar categorías desde la DB con caché
  useEffect(() => {
    loadCategories().then(() => {
      const role = user?.role || 'profesor';
      setCategories(getCategoriesForRole(role, 'view'));
    });
  }, [user]);

  // ── perfil ──────────────────────────────────────────────────────────────────

  // La ruta /admin/usuario/:id espera el ID de WordPress; el RUT (username) no sirve.
  const handleOpenPerfil = useCallback(async (rut) => {
    let id = _wpIdCache.get(rut);
    if (!id) {
      try {
        const customer = await getCustomerByUsername(rut);
        id = customer?.id;
        if (id) _wpIdCache.set(rut, id);
      } catch { /* se informa abajo */ }
    }
    if (!id) {
      toast({ title: 'No se encontró el perfil del alumno', description: `RUT ${rut}`, status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    history.push(`/admin/usuario/${id}`);
  }, [history, toast]);

  // ── modal ───────────────────────────────────────────────────────────────────

  const handleOpenFicha = useCallback((item) => {
    setViewData(null);
    setIsViewOpen(true);
    getSurveyResponse(item.id)
      .then(setViewData)
      .catch(() => setViewData({ error: 'No se pudieron cargar los datos de la ficha' }));
  }, []);

  const handleCloseView = useCallback(() => {
    setIsViewOpen(false);
    setViewData(null);
  }, []);

  // ── enriquecimiento paralelo de cursos ──────────────────────────────────────

  const enrichCourses = useCallback(async (recs) => {
    const uniqueRuts = [...new Set(
      recs.map(r => r.respondent?.username).filter(Boolean)
    )].filter(rut => !_courseCache.has(rut));

    setCourseMap(new Map(_courseCache));
    if (uniqueRuts.length === 0) return;

    setCoursesLoading(true);
    try {
      // Camino rápido: el curso real (meta group_name) de todos los RUT en pocas llamadas
      const BULK = 300;
      const chunks = [];
      for (let i = 0; i < uniqueRuts.length; i += BULK) chunks.push(uniqueRuts.slice(i, i + BULK));
      const results = await Promise.all(chunks.map(c => getGroupNamesByRuts(c)));
      const found = Object.assign({}, ...results.map(r => r.groups));
      const foundIds = Object.assign({}, ...results.map(r => r.ids));
      uniqueRuts.forEach(rut => {
        _courseCache.set(rut, found[rut] || '');
        if (foundIds[rut]) _wpIdCache.set(rut, foundIds[rut]);
      });
      setCourseMap(new Map(_courseCache));
    } catch {
      // Respaldo: endpoint masivo no disponible → una consulta por RUT, en lotes
      const CHUNK = 8;
      for (let i = 0; i < uniqueRuts.length; i += CHUNK) {
        const chunk = uniqueRuts.slice(i, i + CHUNK);
        const results = await Promise.allSettled(chunk.map(rut => getCustomerByUsername(rut)));
        chunk.forEach((rut, idx) => {
          const c = results[idx].status === 'fulfilled' ? results[idx].value : null;
          _courseCache.set(rut, c?.meta_data?.find(m => m.key === 'group_name')?.value || '');
          if (c?.id) _wpIdCache.set(rut, c.id);
        });
        setCourseMap(new Map(_courseCache));
      }
    }
    setCoursesLoading(false);
  }, []);

  // ── carga de datos ──────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPage(1);
    try {
      const { data } = await getSurveyResponsesByCategory(category, {
        orderBy: 'created_at',
        orderDirection: 'desc',
        search: debouncedSearch,
        interviewer,
      });
      // El backend no filtra por categoría+rol (ni siquiera cuando category==='' en modo
      // "Todas las categorías"), así que se refuerza acá: nunca se muestra una respuesta
      // de una categoría que el rol actual no puede ver, sin importar qué se haya pedido.
      const role = user?.role || 'profesor';
      const records = (data || []).filter(r => canView(role, r.survey?.category || 'otros'));
      setResponses(records);
      enrichCourses(records);
    } catch {
      setError('No se pudieron cargar las fichas.');
      toast({ title: 'Error al cargar fichas', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [category, debouncedSearch, interviewer, enrichCourses, toast, user]);

  // debounce del buscador — evita pegarle a la API en cada tecla
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { loadData(); }, [loadData]);

  // resetear selects dependientes de la categoría solo cuando la categoría cambia
  // (loadData ya no lo hace en cada llamada, para no perder la selección al tipear la búsqueda)
  useEffect(() => {
    setSelectedCourse('');
    setSelectedForm('');
    setInterviewer('');
  }, [category]);

  // cargar entrevistadores de la categoría activa (el backend devuelve username + nombre)
  useEffect(() => {
    getSurveyResponseFilterFields(category)
      .then(fields => {
        const f = fields.find(x => x.text === INTERVIEWER_FIELD);
        setInterviewerOptions(
          (f?.values || []).map(v => (typeof v === 'string' ? { value: v, label: v } : v))
        );
      })
      .catch(() => setInterviewerOptions([]));
  }, [category]);

  // reset página cuando cambian filtros
  useEffect(() => { setPage(1); }, [search, selectedForm, selectedCourse, sortBy, sortDir, dateFrom, dateTo]);

  // ── datos derivados ─────────────────────────────────────────────────────────

  const formOptions = useMemo(() => {
    const map = new Map();
    responses.forEach(r => {
      if (r.survey?.id && !map.has(r.survey.id))
        map.set(r.survey.id, r.survey.name || `#${r.survey.id}`);
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [responses]);

  // opciones de curso normalizadas y agrupadas pedagógicamente
  const courseOptions = useMemo(() => {
    const rawValues = [];
    courseMap.forEach(v => { if (v) rawValues.push(v); });
    return buildNormalizedGroupOptions(rawValues);
    // retorna [{ code, label, rawValues }] ordenado pedagógicamente
  }, [courseMap]);

  // ── filtrado y orden ────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = [...responses];

    if (selectedForm) list = list.filter(r => String(r.survey?.id) === selectedForm);

    if (dateFrom) {
      // 'T00:00:00' fuerza parseo en hora local (evita UTC midnight que desplaza el día en zonas UTC-)
      const from = new Date(dateFrom + 'T00:00:00');
      list = list.filter(r => new Date(r.created_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59.999');
      list = list.filter(r => new Date(r.created_at) <= to);
    }

    // filtro por curso: comparar código normalizado
    if (selectedCourse) {
      list = list.filter(r => {
        const raw = courseMap.get(r.respondent?.username) || '';
        return normalizeCourseName(raw) === selectedCourse;
      });
    }

    if (search) {
      const term = search.toLowerCase();
      list = list.filter(r => {
        const name   = respondentFullName(r.respondent).toLowerCase();
        const rut    = (r.respondent?.username || '').toLowerCase();
        const form   = (r.survey?.name || '').toLowerCase();
        const raw    = courseMap.get(r.respondent?.username) || '';
        const course = codeToDisplayLabel(normalizeCourseName(raw)).toLowerCase();
        return name.includes(term) || rut.includes(term) || form.includes(term) || course.includes(term);
      });
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortBy === 'fecha') return dir * (new Date(a.created_at) - new Date(b.created_at));
      if (sortBy === 'curso') {
        const ca = normalizeCourseName(courseMap.get(a.respondent?.username) || '');
        const cb = normalizeCourseName(courseMap.get(b.respondent?.username) || '');
        return dir * ca.localeCompare(cb, 'es');
      }
      if (sortBy === 'name_form')   return dir * (a.survey?.name || '').localeCompare(b.survey?.name || '', 'es');
      if (sortBy === 'name_alumno') return dir * respondentFullName(a.respondent).localeCompare(respondentFullName(b.respondent), 'es');
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return list;
  }, [responses, selectedForm, selectedCourse, dateFrom, dateTo, courseMap, search, sortBy, sortDir]);

  const thisMonth = useMemo(() => {
    const now = new Date();
    return filtered.filter(r => {
      const d = new Date(r.created_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [filtered]);

  const uniqueRespondents = useMemo(
    () => new Set(filtered.map(r => r.respondent?.username).filter(Boolean)).size,
    [filtered]
  );

  const uniqueForms = useMemo(
    () => new Set(filtered.map(r => r.survey?.id).filter(Boolean)).size,
    [filtered]
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated  = filtered.slice((page - 1) * perPage, page * perPage);

  // ── exportación XLSX ────────────────────────────────────────────────────────

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const now = new Date();
      const fmtDT = (d) => d.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
      const fmtD  = (iso) => iso ? new Date(iso).toLocaleDateString('es-CL') : '—';
      const fmtT  = (iso) => iso ? new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : '—';
      const getCourse = (r) => {
        const raw = courseMap.get(r.respondent?.username) || '';
        return raw ? codeToDisplayLabel(normalizeCourseName(raw)) : '';
      };

      // ── captura del gráfico ──
      let chartBase64 = null;
      try {
        const ApexLib = (await import('apexcharts')).default;
        const result  = await ApexLib.exec('atencion-timeline', 'dataURI', {});
        if (result?.imgURI) chartBase64 = result.imgURI.split(',')[1];
      } catch { /* chart no disponible */ }

      // ── agregados ──
      const filterLines = [];
      if (category)       filterLines.push(['Categoría',   getLabelForCategory(category) || category]);
      if (dateFrom)       filterLines.push(['Desde',       fmtD(dateFrom)]);
      if (dateTo)         filterLines.push(['Hasta',       fmtD(dateTo)]);
      if (selectedCourse) filterLines.push(['Curso',       codeToDisplayLabel(selectedCourse)]);
      if (selectedForm) {
        const f = formOptions.find(fo => String(fo.id) === selectedForm);
        if (f) filterLines.push(['Formulario', f.name]);
      }
      if (interviewer) {
        const o = interviewerOptions.find(x => x.value === interviewer);
        filterLines.push(['Entrevistador', o?.label || interviewer]);
      }
      if (search) filterLines.push(['Búsqueda', `"${search}"`]);

      const byForm = {};
      filtered.forEach(r => {
        const k = r.survey?.name || 'Sin formulario';
        if (!byForm[k]) byForm[k] = { total: 0, alumnos: new Set() };
        byForm[k].total++;
        if (r.respondent?.username) byForm[k].alumnos.add(r.respondent.username);
      });

      const byCourse = {};
      filtered.forEach(r => {
        const k = getCourse(r) || 'Sin curso';
        if (!byCourse[k]) byCourse[k] = { total: 0, alumnos: new Set(), forms: new Set() };
        byCourse[k].total++;
        if (r.respondent?.username) byCourse[k].alumnos.add(r.respondent.username);
        if (r.survey?.id) byCourse[k].forms.add(r.survey.id);
      });

      const byMonth = {};
      filtered.forEach(r => {
        if (!r.created_at) return;
        const d = new Date(r.created_at);
        const key   = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleString('es-CL', { month: 'long', year: 'numeric' });
        if (!byMonth[key]) byMonth[key] = { label, total: 0 };
        byMonth[key].total++;
      });

      const thisMonthCount = filtered.filter(r => {
        const d = new Date(r.created_at);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length;

      // ── ExcelJS ──
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'Datofy';
      wb.created = now;

      const H = (row) => { row.font = { bold: true }; return row; };
      const addRows = (ws, rows) => rows.forEach(r => ws.addRow(r));

      // ── Hoja 1: Resumen ──
      const wsR = wb.addWorksheet('Resumen');
      wsR.columns = [{ width: 4 }, { width: 38 }, { width: 22 }, { width: 22 }, { width: 18 }];
      H(wsR.addRow(['REPORTE DE FICHAS DE ATENCIÓN']));
      wsR.addRow(['Generado el:', fmtDT(now)]);
      wsR.addRow([]);
      H(wsR.addRow(['FILTROS APLICADOS']));
      addRows(wsR, filterLines.length ? filterLines.map(([k, v]) => ['', k, v]) : [['', 'Sin filtros activos', '']]);
      wsR.addRow([]);
      H(wsR.addRow(['RESUMEN GENERAL']));
      addRows(wsR, [
        ['', 'Total fichas en selección', filtered.length],
        ['', 'Fichas este mes',           thisMonthCount],
        ['', 'Alumnos distintos',         uniqueRespondents],
        ['', 'Formularios distintos',     uniqueForms],
      ]);
      wsR.addRow([]);
      H(wsR.addRow(['DESGLOSE POR FORMULARIO']));
      H(wsR.addRow(['', 'Formulario', 'Total fichas', 'Alumnos distintos']));
      addRows(wsR, Object.entries(byForm).sort((a,b)=>b[1].total-a[1].total).map(([n,d])=>['',n,d.total,d.alumnos.size]));
      wsR.addRow([]);
      H(wsR.addRow(['DESGLOSE POR CURSO']));
      H(wsR.addRow(['', 'Curso', 'Total fichas', 'Alumnos distintos', 'Formularios']));
      addRows(wsR, Object.entries(byCourse).sort((a,b)=>b[1].total-a[1].total).map(([c,d])=>['',c,d.total,d.alumnos.size,d.forms.size]));
      wsR.addRow([]);
      H(wsR.addRow(['DESGLOSE POR MES']));
      H(wsR.addRow(['', 'Mes', 'Total fichas']));
      addRows(wsR, Object.entries(byMonth).sort((a,b)=>a[0].localeCompare(b[0])).map(([,d])=>['',d.label,d.total]));

      // ── gráfico embebido en Resumen ──
      if (chartBase64) {
        wsR.addRow([]);
        H(wsR.addRow(['GRÁFICO — FICHAS EN EL TIEMPO']));
        const chartRow = wsR.lastRow.number + 1;
        const imgId = wb.addImage({ base64: chartBase64, extension: 'png' });
        wsR.addImage(imgId, {
          tl: { col: 1, row: chartRow - 1 },
          ext: { width: 740, height: 300 },
        });
        for (let i = 0; i < 18; i++) wsR.addRow([]);
      }

      // ── Hoja 2: Fichas ──
      const wsF = wb.addWorksheet('Fichas');
      wsF.columns = [
        { header: 'N°',         width: 5  },
        { header: 'Formulario', width: 36 },
        { header: 'Categoría',  width: 18 },
        { header: 'Alumno',     width: 28 },
        { header: 'RUT',        width: 14 },
        { header: 'Curso',      width: 18 },
        { header: 'Entrevistador', width: 26 },
        { header: 'Fecha',      width: 14 },
        { header: 'Hora',       width: 8  },
      ];
      wsF.getRow(1).font = { bold: true };
      wsF.addRows(filtered.map((r, i) => [
        i + 1,
        r.survey?.name || '',
        getLabelForCategory(r.survey?.category || category) || (r.survey?.category || category) || '',
        respondentFullName(r.respondent),
        r.respondent?.username || '',
        getCourse(r),
        r.interviewer?.name || r.interviewer?.username || '',
        fmtD(r.created_at),
        fmtT(r.created_at),
      ]));

      // ── Hoja 3: Por Formulario ──
      const wsFm = wb.addWorksheet('Por Formulario');
      wsFm.columns = [{ header: 'Formulario', width: 38 }, { header: 'Total fichas', width: 16 }, { header: 'Alumnos distintos', width: 20 }];
      wsFm.getRow(1).font = { bold: true };
      wsFm.addRows(Object.entries(byForm).sort((a,b)=>b[1].total-a[1].total).map(([n,d])=>[n,d.total,d.alumnos.size]));

      // ── Hoja 4: Por Curso ──
      const wsC = wb.addWorksheet('Por Curso');
      wsC.columns = [{ header: 'Curso', width: 22 }, { header: 'Total fichas', width: 16 }, { header: 'Alumnos distintos', width: 20 }, { header: 'Formularios', width: 14 }];
      wsC.getRow(1).font = { bold: true };
      wsC.addRows(Object.entries(byCourse).sort((a,b)=>b[1].total-a[1].total).map(([c,d])=>[c,d.total,d.alumnos.size,d.forms.size]));

      const buffer = await wb.xlsx.writeBuffer();
      const blob   = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url    = URL.createObjectURL(blob);
      const a      = document.createElement('a');
      a.href       = url;
      a.download   = `fichas-atencion-${now.toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [filtered, courseMap, category, dateFrom, dateTo, selectedCourse, selectedForm, search,
      interviewer, interviewerOptions, formOptions, uniqueRespondents, uniqueForms]);

  // ── gráfico línea de tiempo ─────────────────────────────────────────────────

  // auto-detecta granularidad según el span del dataset filtrado
  useEffect(() => {
    if (filtered.length < 2) return;
    const times = filtered.map(r => new Date(r.created_at).getTime()).filter(t => !isNaN(t));
    if (!times.length) return;
    const spanDays = (Math.max(...times) - Math.min(...times)) / 86400000;
    setGranularity(spanDays <= 60 ? 'day' : spanDays <= 365 ? 'week' : 'month');
  }, [filtered]);

  const chartSeries = useMemo(() => {
    if (filtered.length === 0) return [{ name: 'Fichas', data: [] }];
    const getKey = (d) => {
      if (granularity === 'day')
        return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      if (granularity === 'week') {
        const dow  = d.getDay();
        const diff = d.getDate() - dow + (dow === 0 ? -6 : 1);
        return new Date(d.getFullYear(), d.getMonth(), diff).getTime();
      }
      return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    };
    const buckets = {};
    filtered.forEach(r => {
      if (!r.created_at) return;
      const k = getKey(new Date(r.created_at));
      buckets[k] = (buckets[k] || 0) + 1;
    });
    const data = Object.entries(buckets)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([ts, count]) => ({ x: Number(ts), y: count }));
    return [{ name: 'Fichas', data }];
  }, [filtered, granularity]);

  const chartOptions = useMemo(() => ({
    chart: {
      id: 'atencion-timeline',
      type: 'area',
      toolbar: { show: false },
      animations: { enabled: true, speed: 350 },
      fontFamily: 'inherit',
      zoom: { enabled: false },
      selection: { enabled: false },
      events: {
        click: (_ev, ctx, config) => {
          const idx = config.dataPointIndex;
          if (idx < 0) return;
          const ts = ctx.w.globals.seriesX[0]?.[idx];
          if (ts == null) return;
          const d = new Date(ts);
          let from, to;
          if (granularity === 'day') {
            from = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            to   = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          } else if (granularity === 'week') {
            const dow  = d.getDay();
            const diff = d.getDate() - dow + (dow === 0 ? -6 : 1);
            from = new Date(d.getFullYear(), d.getMonth(), diff);
            to   = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
          } else {
            from = new Date(d.getFullYear(), d.getMonth(), 1);
            to   = new Date(d.getFullYear(), d.getMonth() + 1, 0);
          }
          setDateFrom(from.toISOString().slice(0, 10));
          setDateTo(to.toISOString().slice(0, 10));
        },
      },
    },
    stroke: { curve: 'smooth', width: 2 },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light', type: 'vertical',
        opacityFrom: 0.28, opacityTo: 0.02,
      },
    },
    colors: ['#EF5A2A'],
    dataLabels: { enabled: false },
    markers: { size: 5, colors: ['#EF5A2A'], strokeWidth: 2, strokeColors: '#fff', hover: { size: 8 } },
    xaxis: {
      type: 'datetime',
      labels: {
        style: { colors: '#8B7355', fontSize: '11px', fontWeight: 600 },
        datetimeUTC: false,
        datetimeFormatter: {
          year:  'yyyy',
          month: "MMM 'yy",
          week:  'dd MMM',
          day:   'dd MMM',
        },
      },
      axisBorder: { show: false },
      axisTicks:  { show: false },
    },
    yaxis: {
      min: 0,
      labels: {
        style: { colors: '#8B7355', fontSize: '11px', fontWeight: 600 },
        formatter: (v) => Math.floor(v),
      },
    },
    grid: {
      borderColor: '#EDE8E0',
      strokeDashArray: 4,
      padding: { top: -4, right: 8, bottom: 0, left: 4 },
    },
    tooltip: {
      theme: 'light',
      x: {
        formatter: (val) => {
          const d = new Date(val);
          if (granularity === 'month') return d.toLocaleString('es-CL', { month: 'long', year: 'numeric' });
          if (granularity === 'week')  return `Semana del ${d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })}`;
          return d.toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
        },
      },
      y: { formatter: (v) => `${v} ficha${v !== 1 ? 's' : ''}` },
    },
  }), [granularity]);

  // ── render ──────────────────────────────────────────────────────────────────

  // Bloquea el acceso también por URL directa: dashRoutes (routes.js) registra
  // /atencion sin ningún filtro de rol, así que ocultar el link del sidebar no
  // alcanza — sin esto, cualquier rol podría entrar tecleando /admin/atencion.
  // Lista blanca (solo administrador) en vez de bloquear profesor/teacher puntual,
  // para cubrir también inspector y roles personalizados futuros.
  if (user && user.role !== 'administrador') {
    return (
      <Box p={6}>
        <Alert status="error" borderRadius="16px">
          <AlertIcon />
          <AlertDescription>No tienes permisos para ver esta sección.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  const selectStyles = {
    bg: 'white', border: '1.5px solid', borderColor: 'cream.line2',
    borderRadius: '11px', fontSize: '13.5px', color: 'ink.700',
    fontWeight: '600', size: 'md', _focus: { borderColor: 'coral.500' },
  };

  return (
    <Box bg="cream.bg" minH="100vh">

      {/* Modal — carga el survey response completo antes de mostrarlo */}
      <ModalSurveyResponse
        isOpen={isViewOpen}
        onClose={handleCloseView}
        customer={viewData?.respondent}
        data={viewData}
        onSurveyDeleted={() => { handleCloseView(); loadData(); }}
      />

      {/* breadcrumb */}
      <Flex h="52px" align="center" gap="8px" px="28px" bg="white"
        borderBottom="1px solid" borderColor="cream.line">
        <Text fontSize="13.5px" fontWeight="600" color="ink.400" cursor="pointer"
          _hover={{ color: 'coral.500' }} onClick={() => history.push('/admin/dashboard')}>
          Inicio
        </Text>
        <Text fontSize="13px" color="cream.line2">/</Text>
        <Text fontSize="13.5px" fontWeight="700" color="ink.900">Fichas</Text>
        {category && (
          <>
            <Text fontSize="13px" color="cream.line2">/</Text>
            <Text fontSize="13.5px" fontWeight="700" color="coral.500">
              {getLabelForCategory(category) || category}
            </Text>
          </>
        )}
        <Flex ml="auto" align="center" gap="8px">
          {coursesLoading && (
            <Flex align="center" gap="6px">
              <Spinner size="xs" color="ink.400" />
              <Text fontSize="12px" color="ink.400" fontWeight="600">Cargando cursos…</Text>
            </Flex>
          )}
          <Box bg="coral.soft" color="coral.700" fontSize="12px" fontWeight="800"
            px="11px" py="4px" borderRadius="999px">
            {filtered.length} fichas
          </Box>
          <Button size="sm" variant="outline" color="ink.500" borderRadius="9px"
            borderColor="cream.line2" leftIcon={<Icon as={FiDownload} boxSize="13px" />}
            isLoading={exporting} onClick={handleExport} fontWeight="700" fontSize="13px"
            isDisabled={filtered.length === 0}>
            Exportar XLSX
          </Button>
          <Button size="sm" variant="ghost" color="ink.400" borderRadius="9px"
            leftIcon={<Icon as={FiRefreshCw} boxSize="13px" />}
            isLoading={loading} onClick={loadData} fontWeight="700" fontSize="13px">
            Actualizar
          </Button>
        </Flex>
      </Flex>

      <Box px="28px" py="24px">

        {/* stats */}
        <SimpleGrid columns={{ base: 2, md: 4 }} gap="14px" mb="24px">
          <StatCard label="Total fichas"  value={filtered.length}   sub={responses.length !== filtered.length ? `de ${responses.length} totales` : `categoría ${getLabelForCategory(category) || 'todas'}`} />
          <StatCard label="Este mes"      value={thisMonth}          sub="en selección actual" accent />
          <StatCard label="Formularios"   value={uniqueForms}        sub="tipos distintos" />
          <StatCard label="Alumnos"       value={uniqueRespondents}  sub="en selección actual" />
        </SimpleGrid>

        {/* gráfico línea de tiempo */}
        {filtered.length > 0 && (
          <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="16px"
            p="16px 20px 8px" mb="20px" boxShadow="0 1px 2px rgba(54,38,22,.05)">
            <Flex align="center" justify="space-between" mb="4px">
              <Box>
                <Text fontSize="12px" fontWeight="700" color="ink.400"
                  textTransform="uppercase" letterSpacing=".07em">
                  Fichas en el tiempo
                </Text>
                <Flex align="center" gap="8px">
                  <Text fontSize="11px" color="ink.400" fontWeight="600">
                    {filtered.length} fichas · {chartSeries[0]?.data?.length || 0} puntos
                  </Text>
                  {(dateFrom || dateTo) ? (
                    <Flex align="center" gap="4px" bg="coral.soft" px="8px" py="2px"
                      borderRadius="999px" cursor="pointer"
                      onClick={() => { setDateFrom(''); setDateTo(''); }}
                      title="Limpiar filtro de fecha">
                      <Text fontSize="10px" fontWeight="700" color="coral.600">
                        {dateFrom}{dateFrom && dateTo && dateFrom !== dateTo ? ` → ${dateTo}` : ''}
                      </Text>
                      <Icon as={FiX} boxSize="9px" color="coral.500" />
                    </Flex>
                  ) : (
                    <Text fontSize="10px" color="ink.300" fontWeight="600">
                      Clic en un punto para filtrar
                    </Text>
                  )}
                </Flex>
              </Box>
              <HStack spacing="4px">
                {[['day','Día'],['week','Semana'],['month','Mes']].map(([g, label]) => (
                  <Button key={g} size="xs" borderRadius="8px" fontWeight="700" fontSize="11px"
                    px="10px"
                    bg={granularity === g ? 'coral.500' : 'transparent'}
                    color={granularity === g ? 'white' : 'ink.400'}
                    _hover={{ bg: granularity === g ? 'coral.600' : 'cream.soft' }}
                    onClick={() => setGranularity(g)}>
                    {label}
                  </Button>
                ))}
              </HStack>
            </Flex>
            <Box sx={{ '& .apexcharts-series path, & .apexcharts-series circle': { cursor: 'pointer' } }}>
              <ReactApexChart
                key={granularity}
                options={chartOptions}
                series={chartSeries}
                type="area"
                height={180}
              />
            </Box>
          </Box>
        )}

        {/* toolbar fila 1 */}
        <Flex align="center" gap="10px" mb="10px" flexWrap="wrap">
          <Select value={category} onChange={e => setCategory(e.target.value)}
            maxW="200px" {...selectStyles}>
            <option value="">Todas las categorías</option>
            {categories.map(cat => (
              <option key={cat.slug} value={cat.slug}>{cat.label || getLabelForCategory(cat.slug)}</option>
            ))}
          </Select>

          <InputGroup maxW="300px" size="md">
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="ink.400" boxSize="15px" />
            </InputLeftElement>
            <Input
              placeholder="Buscar alumno, RUT, formulario…"
              value={search} onChange={e => setSearch(e.target.value)}
              bg="white" border="1.5px solid" borderColor="cream.line2" borderRadius="11px"
              fontSize="14px" color="ink.900" _placeholder={{ color: 'ink.400' }}
              _focus={{ borderColor: 'coral.500', boxShadow: '0 0 0 3px rgba(239,90,42,.1)' }}
            />
          </InputGroup>

          <Text fontSize="13px" color="ink.400" fontWeight="600" ml="auto">
            {filtered.length} {filtered.length === 1 ? 'resultado' : 'resultados'}
          </Text>
        </Flex>

        {/* toolbar fila 2 */}
        <Flex align="center" gap="10px" mb="18px" flexWrap="wrap">
          {interviewerOptions.length > 0 && (
            <Select value={interviewer} onChange={e => setInterviewer(e.target.value)}
              maxW="220px" {...selectStyles}>
              <option value="">Todos los entrevistadores</option>
              {interviewerOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          )}

          {formOptions.length > 1 && (
            <Select value={selectedForm} onChange={e => setSelectedForm(e.target.value)}
              maxW="240px" {...selectStyles}>
              <option value="">Todos los formularios</option>
              {formOptions.map(f => <option key={f.id} value={String(f.id)}>{f.name}</option>)}
            </Select>
          )}

          <Select
            value={selectedCourse}
            onChange={e => setSelectedCourse(e.target.value)}
            maxW="200px" {...selectStyles}
            isDisabled={coursesLoading && courseOptions.length === 0}>
            <option value="">
              {coursesLoading && courseOptions.length === 0 ? 'Cargando cursos…' : 'Todos los cursos'}
            </option>
            {courseOptions.map(opt => (
              <option key={opt.code} value={opt.code}>{opt.label}</option>
            ))}
          </Select>

          <Input
            type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            placeholder="Desde" maxW="160px"
            bg="white" border="1.5px solid" borderColor="cream.line2" borderRadius="11px"
            fontSize="13px" color="ink.700" fontWeight="600"
            _focus={{ borderColor: 'coral.500', boxShadow: '0 0 0 3px rgba(239,90,42,.1)' }}
          />
          <Input
            type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            placeholder="Hasta" maxW="160px"
            bg="white" border="1.5px solid" borderColor="cream.line2" borderRadius="11px"
            fontSize="13px" color="ink.700" fontWeight="600"
            _focus={{ borderColor: 'coral.500', boxShadow: '0 0 0 3px rgba(239,90,42,.1)' }}
          />
          {(dateFrom || dateTo) && (
            <IconButton
              size="sm" variant="ghost" color="ink.400"
              icon={<Icon as={FiX} boxSize="14px" />}
              aria-label="Limpiar fechas" borderRadius="9px"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
            />
          )}

          <Select
            value={sortBy}
            onChange={e => { setSortBy(e.target.value); setSortDir(e.target.value === 'fecha' ? 'desc' : 'asc'); }}
            maxW="210px" {...selectStyles}>
            <option value="fecha">Por fecha</option>
            <option value="curso">Por curso</option>
            <option value="name_form">Por formulario</option>
            <option value="name_alumno">Por alumno</option>
          </Select>
        </Flex>

        {/* contenido */}
        {loading ? (
          <Flex align="center" justify="center" h="260px">
            <Spinner size="xl" color="coral.500" thickness="3px" />
          </Flex>
        ) : error ? (
          <Flex align="center" justify="center" h="200px" direction="column" gap="12px">
            <Icon as={FiClipboard} boxSize="40px" color="ink.400" />
            <Text color="ink.400" fontWeight="600">{error}</Text>
            <Button size="sm" colorScheme="orange" onClick={loadData}>Reintentar</Button>
          </Flex>
        ) : filtered.length === 0 ? (
          <Flex align="center" justify="center" h="200px" direction="column" gap="12px">
            <Icon as={FiFileText} boxSize="40px" color="ink.400" />
            <Text color="ink.400" fontWeight="600">
              {search || selectedForm || selectedCourse
                ? 'Sin fichas que coincidan con ese filtro'
                : 'No hay fichas registradas en esta categoría'}
            </Text>
          </Flex>
        ) : (
          <>
            <Box bg="white" border="1px solid" borderColor="cream.line"
              borderRadius="20px" overflow="hidden"
              boxShadow="0 1px 2px rgba(54,38,22,.05)">
              <Table variant="unstyled" size="sm">
                <Thead>
                  <Tr borderBottom="1px solid" borderColor="cream.line">
                    {[
                      { label: 'Formulario', key: 'name_form' },
                      { label: 'Alumno / RUT', key: 'name_alumno' },
                      { label: 'Curso', key: 'curso' },
                      { label: 'Fecha', key: 'fecha' },
                      { label: '', key: null },
                    ].map(({ label, key }, i) => {
                      const active = sortBy === key;
                      const arrow  = sortDir === 'asc' ? ' ↑' : ' ↓';
                      return (
                        <Th key={i} px="18px" py="12px" fontSize="11px" fontWeight="800"
                          color={active ? 'coral.500' : 'ink.400'}
                          textTransform="uppercase" letterSpacing=".05em"
                          bg="cream.bg2" textAlign="left"
                          cursor={key ? 'pointer' : 'default'}
                          userSelect="none"
                          _hover={key ? { color: 'coral.500' } : {}}
                          onClick={key ? () => {
                            if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                            else { setSortBy(key); setSortDir('asc'); }
                          } : undefined}>
                          {label}{active ? arrow : ''}
                        </Th>
                      );
                    })}
                  </Tr>
                </Thead>
                <Tbody>
                  {paginated.map((item, idx) => {
                    const respondent = item.respondent;
                    const name       = respondentFullName(respondent);
                    const rut        = respondent?.username;
                    const rawCourse  = courseMap.get(rut) || '';
                    const courseCode = rawCourse ? normalizeCourseName(rawCourse) : '';
                    const courseLabel = courseCode ? codeToDisplayLabel(courseCode) : '';
                    const isLast     = idx === paginated.length - 1;

                    return (
                      <Tr key={item.id}
                        borderTop="1px solid" borderColor="cream.line"
                        _hover={{ bg: 'coral.tint', cursor: 'pointer' }}
                        transition=".13s"
                        onClick={() => handleOpenFicha(item)}>

                        {/* formulario */}
                        <Td px="18px" py="13px" borderBottom={isLast ? 'none' : undefined}>
                          <Flex align="center" gap="12px">
                            <Flex w="34px" h="34px" borderRadius="9px" bg="sky.bg" color="sky.ink"
                              align="center" justify="center" flexShrink={0}>
                              <Icon as={FiFileText} boxSize="15px" />
                            </Flex>
                            <Box minW={0}>
                              <Text fontWeight="800" fontSize="14px" color="ink.900"
                                letterSpacing="-0.01em" noOfLines={1}>
                                {item.survey?.name || 'Sin nombre'}
                              </Text>
                              <Text fontSize="11.5px" color="ink.400" fontWeight="600" mt="2px">
                                #{item.id}
                              </Text>
                            </Box>
                          </Flex>
                        </Td>

                        {/* alumno */}
                        <Td px="18px" py="13px" borderBottom={isLast ? 'none' : undefined}>
                          <Flex align="center" gap="9px">
                            <Avatar name={name} size={30} />
                            <Box minW={0}>
                              <Text fontWeight="600" fontSize="13.5px" color="ink.900" noOfLines={1}>
                                {name}
                              </Text>
                              {rut && (
                                <Text fontSize="11px" color="ink.400" fontWeight="500">{rut}</Text>
                              )}
                            </Box>
                          </Flex>
                        </Td>

                        {/* curso — normalizado */}
                        <Td px="18px" py="13px" borderBottom={isLast ? 'none' : undefined}>
                          {courseLabel ? (
                            <Badge colorScheme="blue" borderRadius="full" px="10px" py="3px"
                              fontSize="12px" fontWeight="700">
                              {courseLabel}
                            </Badge>
                          ) : (
                            <Text fontSize="12px" color="ink.400" fontWeight="500">
                              {coursesLoading ? '…' : '—'}
                            </Text>
                          )}
                        </Td>

                        {/* fecha */}
                        <Td px="18px" py="13px" borderBottom={isLast ? 'none' : undefined}>
                          <Text fontSize="13px" color="ink.700" fontWeight="600">
                            {fmtRelative(item.created_at)}
                          </Text>
                          <Text fontSize="11px" color="ink.400" fontWeight="500" mt="2px">
                            {fmtDate(item.created_at)}
                          </Text>
                        </Td>

                        {/* acciones */}
                        <Td px="14px" py="13px" borderBottom={isLast ? 'none' : undefined}>
                          <Flex gap="6px" sx={{ 'tr:hover &': { opacity: 1 } }} opacity={0} transition=".14s">
                            <Button size="sm" variant="ghost" color="ink.700" borderRadius="9px"
                              leftIcon={<Icon as={FiEye} boxSize="13px" />}
                              fontWeight="700" fontSize="12px"
                              _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                              onClick={e => { e.stopPropagation(); handleOpenFicha(item); }}>
                              Ver
                            </Button>
                            {rut && (
                              <Button size="sm" variant="ghost" color="ink.400" borderRadius="9px"
                                leftIcon={<Icon as={FiUser} boxSize="13px" />}
                                fontWeight="700" fontSize="12px"
                                _hover={{ bg: 'cream.bg2', color: 'ink.700' }}
                                onClick={e => { e.stopPropagation(); handleOpenPerfil(rut); }}>
                                Perfil
                              </Button>
                            )}
                          </Flex>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>

            {/* paginación */}
            {filtered.length > perPage && (
              <Flex justify="space-between" align="center" mt="16px" flexWrap="wrap" gap="10px">
                <HStack spacing="3">
                  <Select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                    size="sm" w="120px" borderRadius="9px" fontSize="13px" fontWeight="600"
                    bg="white" borderColor="cream.line2">
                    {PER_PAGE_OPTIONS.map(n => (
                      <option key={n} value={n}>{n} / página</option>
                    ))}
                  </Select>
                  <Text fontSize="13px" color="ink.400" fontWeight="600">
                    {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} de {filtered.length}
                  </Text>
                </HStack>

                <HStack spacing="1">
                  <IconButton size="sm" variant="ghost" icon={<ArrowLeftIcon h={3} w={3} />}
                    onClick={() => setPage(1)} isDisabled={page === 1} aria-label="Primera" />
                  <IconButton size="sm" variant="ghost" icon={<ChevronLeftIcon h={4} w={4} />}
                    onClick={() => setPage(p => p - 1)} isDisabled={page === 1} aria-label="Anterior" />
                  <Text fontSize="13px" color="ink.700" fontWeight="600" px="8px">
                    {page} / {totalPages}
                  </Text>
                  <IconButton size="sm" variant="ghost" icon={<ChevronRightIcon h={4} w={4} />}
                    onClick={() => setPage(p => p + 1)} isDisabled={page === totalPages} aria-label="Siguiente" />
                  <IconButton size="sm" variant="ghost" icon={<ArrowRightIcon h={3} w={3} />}
                    onClick={() => setPage(totalPages)} isDisabled={page === totalPages} aria-label="Última" />
                </HStack>
              </Flex>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AtencionView;
